import React, { useState, useEffect, useMemo } from 'react';
import { Button, Card, Col, Form, Modal, Row, Select, Input, Space, Tag, Spin } from 'antd';
import { toast } from 'react-toastify';
import { ResponseDataType, postRequest, getRequest } from 'api';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { PlusOutlined } from '@ant-design/icons';
import { ClassLevelSelect, DatePicker, parseFlexibleDate } from 'Components';

interface CreateStudentDataType {
  name: string;
  dob: any;
  gender: number;
  cmnd: string;
  address?: string;
  email?: string;
  parentPhone?: string;
  parentName?: string;
  parentEmail?: string;
  parentDob?: any;
  classLevel?: string;
  classId?: number;
}

interface ClassDataType {
  classId: number;
  classYearId: number;
  className: string;
  classLevel: string;
  classOrder: number;
  description: string | null;
}

interface CreateStudentProps {
  isModal?: boolean;
  onSuccess?: () => void;
  onClose?: () => void;
}

export const CreateStudent: React.FC<CreateStudentProps> = ({
  isModal = false,
  onSuccess,
  onClose,
}) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const schoolId = Number(localStorage.getItem('schoolId'));
  const [classList, setClassList] = useState<ClassDataType[]>([]);
  const [selectedClassLevel, setSelectedClassLevel] = useState<string | undefined>();
  const [selectedClassId, setSelectedClassId] = useState<number | undefined>();
  const [selectedYear, setSelectedYear] = useState<any>(dayjs());
  const [submitting, setSubmitting] = useState(false);

  // Trạng thái phụ huynh đã tồn tại (check theo SĐT)
  const [isExistingParent, setIsExistingParent] = useState(false);
  const [existingParentId, setExistingParentId] = useState<number | null>(null);
  const [checkingPhone, setCheckingPhone] = useState(false);

  /* =======================
     Danh sách khối từ lớp của năm học
  ======================= */
  const classLevels = useMemo(() => {
    const set = new Set<string>();
    classList.forEach((c) => {
      if (c.classLevel && c.classLevel.trim()) {
        set.add(c.classLevel.trim());
      }
    });
    return Array.from(set).sort((a, b) => {
      const aNum = parseInt(a, 10);
      const bNum = parseInt(b, 10);
      if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
      return a.localeCompare(b, undefined, { numeric: true });
    });
  }, [classList]);

  /* =======================
     Danh sách lớp theo khối đã chọn
  ======================= */
  const filteredClasses = useMemo(() => {
    if (!selectedClassLevel) return [];
    return classList.filter((c) => c.classLevel === selectedClassLevel);
  }, [classList, selectedClassLevel]);

  /* =======================
     Kiểm tra SĐT phụ huynh
  ======================= */
  const handlePhoneCheck = async (customPhone?: string) => {
    const rawPhone = customPhone !== undefined ? customPhone : form.getFieldValue('parentPhone');
    if (!rawPhone || rawPhone.trim().length < 9) {
      if (isExistingParent) {
        setIsExistingParent(false);
        setExistingParentId(null);
      }
      return;
    }

    const cleanPhone = rawPhone.trim();
    setCheckingPhone(true);
    try {
      const res: ResponseDataType<any> = await getRequest(
        `/cms/student/get-parent-by-phoneNumber?phoneNumber=${encodeURIComponent(cleanPhone)}`
      );
      if (res?.code === 200 && res.info?.parentId) {
        setIsExistingParent(true);
        setExistingParentId(res.info.parentId);
        form.setFieldsValue({
          parentName: res.info.name || '',
          parentEmail: res.info.email || '',
          parentDob: parseFlexibleDate(res.info.dob),
        });
        toast.info(`Đã tìm thấy phụ huynh: ${res.info.name || cleanPhone}. Sẽ liên kết với học sinh này.`);
      } else {
        if (isExistingParent) {
          setIsExistingParent(false);
          setExistingParentId(null);
        }
      }
    } catch (error) {
      setIsExistingParent(false);
      setExistingParentId(null);
    } finally {
      setCheckingPhone(false);
    }
  };

  /* =======================
     Create student + parent
  ======================= */
  const handleCreate = async (values: CreateStudentDataType) => {
    const dobString = values.dob
      ? dayjs(values.dob).isValid()
        ? dayjs(values.dob).format("YYYY-MM-DD")
        : undefined
      : undefined;

    const parentDobString = values.parentDob
      ? dayjs(values.parentDob).isValid()
        ? dayjs(values.parentDob).format("YYYY-MM-DD")
        : undefined
      : undefined;

    const parentPhone = values.parentPhone?.trim();
    const parentName = values.parentName?.trim();

    const targetClassId = values.classId || selectedClassId;
    if (!targetClassId) {
      toast.error('Vui lòng chọn lớp học cho học sinh!');
      return;
    }

    const cls = classList.find((c) => c.classId === targetClassId);
    if (!cls || !cls.classYearId) {
      toast.error('Không tìm thấy thông tin niên khóa của lớp đã chọn!');
      return;
    }

    Modal.confirm({
      title: 'Xác nhận thêm học sinh',
      okText: 'Đồng ý',
      cancelText: 'Hủy',
      async onOk() {
        setSubmitting(true);
        try {
          // 1. Xác định hoặc tạo mới Phụ huynh
          let finalParentId = existingParentId;

          if (!finalParentId && parentPhone) {
            const checkRes: ResponseDataType<any> = await getRequest(
              `/cms/student/get-parent-by-phoneNumber?phoneNumber=${encodeURIComponent(parentPhone)}`
            );
            if (checkRes?.code === 200 && checkRes.info?.parentId) {
              finalParentId = checkRes.info.parentId;
            } else {
              const parentPayload = {
                name: parentName,
                phoneNumber: parentPhone,
                email: values.parentEmail?.trim(),
                dob: parentDobString,
              };

              const createParentRes: ResponseDataType<any> = await postRequest(
                '/cms/parent',
                parentPayload,
                'json'
              );

              if (createParentRes?.code === 200 && createParentRes.info?.parentId) {
                finalParentId = createParentRes.info.parentId;
              } else if (createParentRes?.info?.parentId) {
                finalParentId = createParentRes.info.parentId;
              } else {
                toast.error(createParentRes?.msg || 'Tạo thông tin phụ huynh thất bại!');
                return;
              }
            }
          }

          // 2. Tạo học sinh
          const studentPayload = {
            bhytNum: values.cmnd?.trim(),
            cmnd: values.cmnd?.trim(),
            status: 1,
            gender: values.gender,
            name: values.name?.trim(),
            studentCode: values.cmnd?.trim(),
            dob: dobString,
            email: values.email?.trim() || undefined,
            address: values.address?.trim() || undefined,
            parentPhonenum: parentPhone || undefined,
            classId: targetClassId,
            classYearId: cls.classYearId,
            schoolId,
          };

          const studentRes: ResponseDataType<any> = await postRequest(
            '/cms/student',
            studentPayload,
            'json'
          );

          if (studentRes?.code !== 200) {
            toast.error(studentRes?.msg || 'Thêm học sinh thất bại!');
            return;
          }

          const studentId =
            studentRes.info?.studentId || (studentRes as any).data?.studentId;

          // 3. Liên kết phụ huynh vào học sinh
          if (finalParentId && studentId) {
            await postRequest(`/cms/student/${studentId}/add-parent-to-student`, {
              parentId: finalParentId,
            });
          }

          // 4. Thêm học sinh vào lớp niên khóa
          if (targetClassId && studentId && cls?.classYearId) {
            await postRequest(
              `/cms/class/${cls.classYearId}/add-student-to-class-year`,
              { studentId },
              'json'
            );
          }

          toast.success('Thêm học sinh thành công!');
          form.resetFields();
          if (onSuccess) onSuccess();
          if (onClose) {
            onClose();
          } else {
            navigate('/student-list');
          }
        } catch (error) {
          console.error('Lỗi khi thêm học sinh:', error);
          toast.error('Có lỗi xảy ra khi thêm học sinh');
        } finally {
          setSubmitting(false);
        }
      },
    });
  };

  /* =======================
     Fetch class list
  ======================= */
  const fetchClasses = async () => {
    const params = new URLSearchParams({
      start: selectedYear ? selectedYear.year().toString() : dayjs().year().toString(),
      schoolId: schoolId.toString(),
      page: '1',
      perPage: '100',
      sort: 'asc',
      sortBy: 'className',
    });

    const res: ResponseDataType<{ list: ClassDataType[] }> =
      await getRequest(
        `/cms/class/get-class-years-by-start-end-schoolId?${params}`
      );

    if (res?.code === 200) {
      setClassList(res.info?.list || []);
    } else {
      toast.error(res?.msg || 'Lỗi tải danh sách lớp');
    }
  };

  useEffect(() => {
    fetchClasses();
  }, [selectedYear]);

  const formContent = (
    <Form form={form} layout="vertical" onFinish={handleCreate}>
      {/* Mục 1: Thông tin học sinh & Lớp học */}
      <div
        style={{
          fontWeight: 600,
          marginBottom: 10,
          color: '#141D49',
          borderBottom: '1px solid #f0f0f0',
          paddingBottom: 4,
        }}
      >
        1. Thông tin học sinh & Lớp học
      </div>

      <Row gutter={12}>
        <Col xs={24} sm={10}>
          <Form.Item
            label="Tên học sinh"
            name="name"
            style={{ marginBottom: 10 }}
            rules={[{ required: true, message: 'Vui lòng nhập tên học sinh' }]}
          >
            <Input placeholder="Nhập họ và tên học sinh" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item
            label="Mã định danh (CCCD)"
            name="cmnd"
            style={{ marginBottom: 10 }}
            rules={[
              { required: true, message: 'Vui lòng nhập số CCCD' },
              { pattern: /^\d{12}$/, message: 'CCCD gồm đúng 12 chữ số' },
            ]}
          >
            <Input placeholder="Nhập 12 số CCCD" maxLength={12} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={6}>
          <Form.Item
            label="Giới tính"
            name="gender"
            style={{ marginBottom: 10 }}
            rules={[{ required: true, message: 'Chọn giới tính' }]}
          >
            <Select
              allowClear
              options={[
                { value: 1, label: 'Nam' },
                { value: 0, label: 'Nữ' },
              ]}
              placeholder="Chọn giới tính"
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={12}>
        <Col xs={24} sm={6}>
          <Form.Item
            label="Ngày sinh"
            name="dob"
            style={{ marginBottom: 10 }}
            rules={[{ required: true, message: 'Chọn ngày sinh' }]}
          >
            <DatePicker
              style={{ width: '100%' }}
              placeholder="YYYY-MM-DD"
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={6}>
          <Form.Item label="Năm học" style={{ marginBottom: 10 }}>
            <DatePicker
              picker="year"
              allowClear={false}
              value={selectedYear}
              onChange={(date) => {
                setSelectedYear(date);
                setSelectedClassLevel(undefined);
                setSelectedClassId(undefined);
                form.setFieldsValue({
                  classLevel: undefined,
                  classId: undefined,
                });
              }}
              placeholder="Chọn năm"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={6}>
          <Form.Item
            label="Khối"
            name="classLevel"
            style={{ marginBottom: 10 }}
            rules={[{ required: true, message: 'Chọn khối' }]}
          >
            <ClassLevelSelect
              placeholder="Chọn khối"
              allowClear
              value={selectedClassLevel}
              onChange={(val) => {
                setSelectedClassLevel(val);
                setSelectedClassId(undefined);
                form.setFieldsValue({
                  classLevel: val,
                  classId: undefined,
                });
              }}
              style={{ width: '100%' }}
              levels={classLevels.length > 0 ? classLevels : undefined}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={6}>
          <Form.Item
            label="Lớp học"
            name="classId"
            style={{ marginBottom: 10 }}
            rules={[{ required: true, message: 'Chọn lớp học' }]}
          >
            <Select
              placeholder={
                !selectedClassLevel
                  ? 'Chọn khối trước'
                  : filteredClasses.length === 0
                  ? 'Chưa có lớp'
                  : 'Chọn lớp học'
              }
              disabled={!selectedClassLevel || filteredClasses.length === 0}
              allowClear
              value={selectedClassId}
              onChange={(val) => {
                setSelectedClassId(val);
                form.setFieldsValue({ classId: val });
              }}
              style={{ width: '100%' }}
              options={filteredClasses.map((c) => ({
                value: c.classId,
                label: c.className,
              }))}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={12}>
        <Col xs={24} sm={10}>
          <Form.Item
            label="Email học sinh"
            name="email"
            style={{ marginBottom: 14 }}
            rules={[{ type: 'email', message: 'Email không hợp lệ' }]}
          >
            <Input placeholder="Nhập địa chỉ email (nếu có)" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={14}>
          <Form.Item label="Địa chỉ cư trú" name="address" style={{ marginBottom: 14 }}>
            <Input placeholder="Nhập địa chỉ cư trú" />
          </Form.Item>
        </Col>
      </Row>

      {/* Mục 2: Thông tin phụ huynh */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontWeight: 600,
          marginBottom: 10,
          color: '#141D49',
          borderBottom: '1px solid #f0f0f0',
          paddingBottom: 4,
        }}
      >
        <span>2. Thông tin phụ huynh</span>
        {checkingPhone ? (
          <Spin size="small" />
        ) : isExistingParent ? (
          <Space size={6}>
            <Tag color="green" style={{ margin: 0 }}>
              ✓ Đã có tài khoản (tự động liên kết)
            </Tag>
            <Button
              type="link"
              size="small"
              style={{ padding: 0, height: 'auto' }}
              onClick={() => {
                setIsExistingParent(false);
                setExistingParentId(null);
                form.setFieldsValue({
                  parentPhone: '',
                  parentName: '',
                  parentEmail: '',
                  parentDob: undefined,
                });
              }}
            >
              Đổi SĐT
            </Button>
          </Space>
        ) : null}
      </div>

      <Row gutter={12}>
        <Col xs={24} sm={10}>
          <Form.Item
            label="Số điện thoại (App)"
            name="parentPhone"
            style={{ marginBottom: 10 }}
            rules={[
              { required: true, message: 'Vui lòng nhập SĐT phụ huynh' },
              { pattern: /^[0-9]{9,11}$/, message: 'Số điện thoại không hợp lệ' },
            ]}
          >
            <Input
              placeholder="Nhập số điện thoại phụ huynh"
              onBlur={() => handlePhoneCheck()}
              onChange={(e) => {
                const val = e.target.value?.trim();
                if (val && val.length >= 9) {
                  handlePhoneCheck(val);
                } else if (isExistingParent) {
                  setIsExistingParent(false);
                  setExistingParentId(null);
                }
              }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={14}>
          <Form.Item
            label="Họ và tên phụ huynh"
            name="parentName"
            style={{ marginBottom: 10 }}
            rules={[{ required: true, message: 'Vui lòng nhập tên phụ huynh' }]}
          >
            <Input
              placeholder="Nhập họ và tên phụ huynh"
              disabled={isExistingParent}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={12}>
        <Col xs={24} sm={10}>
          <Form.Item label="Ngày sinh phụ huynh" name="parentDob" style={{ marginBottom: 10 }}>
            <DatePicker
              style={{ width: '100%' }}
              placeholder="Chọn hoặc dán ngày sinh"
              disabled={isExistingParent}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={14}>
          <Form.Item
            label="Email phụ huynh"
            name="parentEmail"
            style={{ marginBottom: 10 }}
            rules={[{ type: 'email', message: 'Email không hợp lệ' }]}
          >
            <Input
              placeholder="Nhập email phụ huynh"
              disabled={isExistingParent}
            />
          </Form.Item>
        </Col>
      </Row>

      {/* Thanh nút bấm */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 8,
          marginTop: 8,
          paddingTop: 12,
          borderTop: '1px solid #f0f0f0',
        }}
      >
        <Button
          onClick={() => {
            if (onClose) onClose();
            else navigate('/student-list');
          }}
          disabled={submitting}
        >
          Hủy
        </Button>
        <Button type="primary" icon={<PlusOutlined />} htmlType="submit" loading={submitting}>
          Lưu học sinh
        </Button>
      </div>
    </Form>
  );

  if (isModal) {
    return formContent;
  }

  return (
    <div style={{ maxWidth: 740, margin: '0 auto' }}>
      <Card title="Thêm học sinh" size="small" bodyStyle={{ padding: '16px 20px' }}>
        {formContent}
      </Card>
    </div>
  );
};

